import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './BiharElectionMap.css';
import BiharMapSVG from './BiharMapSVG'; 
import { api } from '../../services/api';
import config from '../../config/environment';

// Party Color Mapping
const PARTY_COLORS = {
  // Major national/state parties
  'BJP':     '#ff944d', // Saffron/Orange
  'JD(U)':   '#39AC57', // Deep Green
  'RJD':     '#D6901B', // Green
  'INC':     '#19AAED', // Congress Blue
  'LJP':     '#8e24aa', // Violet
  'LJP(R)':  '#A2006D', // Magenta
  'BSP':     '#3949ab', // Blue
  'CPI':     '#D32F2F', // Red
  'CPI(M)':  '#E71A13', // Purple
  'CPI(ML)': '#F44336', // Bright Red
  'AAP':     '#1976d2', // Navy Blue
  'NOTA':    '#888888', // Neutral
  'IND':     '#757575', // Grey
  'IIP':     '#D85F3F', // Deep Orange

  // Medium/known smaller parties
  'AIIM':    '#00664d',
  'AIIMS':   '#00bcd4',
  'HAM':     '#160E4C', // Amber
  'RLJP':    '#ce93d8',
  'RLSP':    '#D4E157',
  'RLM':     '#00695c',
  'JJP':     '#00B86B',
  'SBSP':    '#C2185B',
  'VIP':     '#3f51b5',
  'JJD':     '#00838f',
  'JSP':     '#00aeff',
  'NSP':     '#00a5a5',
  'AIFB':    '#f06292',
  'BLSP':    '#8B0000',
  'JAP':     '#fbc02d',
  'SP':      '#b71c1c',

  // Others: assign distinct pastel/flat colors, cycle for unique visibility
  'RSP':     '#4dd0e1',
  'RUC':     '#7986cb',
  'AHFB':    '#ffb300',
  'GJP':     '#0097a7',
  'JVP':     '#ef6c00',
  'SBKP':    '#004d40',
  'AJPP':    '#aeea00',
  'DJP':     '#6d4c41',
  'HAMU':    '#424242',
  'PPI':     '#cfd8dc',
  'SDPI':    '#bdbdbd',
  'AJP':     '#a1887f',
  'SUCI':    '#263238',
  'RRP':     '#0288d1',
  'TPP':     '#ad1457',
  'BPY':     '#b2ff59',
  'YDP':     '#4caf50',
  'RLJP':    '#c5cae9',
  'AAPP':    '#d32f2f',
  'BYMP':    '#ffccbc',
  'VVIP':    '#ffab91',
  'SP(I)':   '#a5d6a7',
  'BJJP':    '#fbc02d',
  'LD':      '#f57c00',
  'PB':      '#81d4fa',
  'BSKP':    '#9e9d24',
  'SLP':     '#ff7043',
  'TNRMPI':  '#d81b60',
  'BDP':     '#00897b',
  'BJP1':    '#bcaaa4',
  'SWP':     '#e1bee7',
  'BP(L)':   '#c62828',
  'SUC':     '#ad1457',
  'GSP':     '#43a047',
  'MSP':     '#f8bbd0',
  'NARESH':  '#4e342e',
  'LJSP':    '#e040fb',
  'RSD':     '#9c27b0',
  'LJD':     '#3e2723',
  'RPI':     '#607d8b',
  'JDR':     '#bdbdbd',
  'CCP':     '#789262',
  'AARP':    '#FF7043',
  'SJSP':    '#B3E5FC',
  'JPI':     '#D7CCC8',
  'UBP':     '#ED4C67',
  'BP(P)':   '#A3CB38',
  'SLSP':    '#009432',
  'RASD':    '#FDA7DF',
  'NYKP':    '#9980FA',
  'RASP':    '#D980FA',
  'SSD':     '#5758BB',
  'PP':      '#1289A7',
  'AJPR':    '#006266',
  'Other': '#999966',
  'DEFAULT': '#CCCCCC'
};

const getFullImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const baseUrl = config.API_URL.replace(/\/api$/, '');
  return `${baseUrl}${path}`;
};

const BiharElectionMap = () => {
  const { t } = useTranslation();
  const [constituencies, setConstituencies] = useState([]);
  const [winnerData, setWinnerData] = useState({});
  const [selectedConstituency, setSelectedConstituency] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('map');
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadConstituenciesAndResults();
  }, []);

  const loadConstituenciesAndResults = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getConstituencies();
      const transformedData = data.map(constituency => ({
        ...constituency,
        displayCode: constituency.code.replace('AC', '')
      }));
      setConstituencies(transformedData);

      const resultsResponse = await api.getAllCandidatesWithResults();
      if (
        resultsResponse &&
        resultsResponse.success &&
        Array.isArray(resultsResponse.data)
      ) {
        const winnersMap = {};
        resultsResponse.data.forEach((candidate) => {
          const code = candidate.constituencyCode;
          const rounds = Array.isArray(candidate.rounds) ? candidate.rounds : [];
          const latestRound = rounds.length > 0 ? rounds[rounds.length - 1] : {};
          const latestVotes = typeof latestRound.votes === 'number' ? latestRound.votes : 0;
          if (
            !winnersMap[code] ||
            (latestVotes > (winnersMap[code]?.votes || 0))
          ) {
            winnersMap[code] = {
              party: candidate.partyShortName || 'IND',
              name: candidate.candidateName || candidate.name,
              votes: latestVotes,
            };
          }
        });
        setWinnerData(winnersMap);
      }
    } catch (error) {
      setError(t('common.loadError') || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const phase1Count = useMemo(
    () => constituencies.filter(c => String(c.phase) === '1').length,
    [constituencies]
  );
  const phase2Count = useMemo(
    () => constituencies.filter(c => String(c.phase) === '2').length,
    [constituencies]
  );

  const handleConstituencyClick = async constituency => {
    setSelectedConstituency(constituency);
    setLoading(true);
    setError(null);
    setCurrentView('candidates');
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      const constituencyCandidates = await api.getCandidatesByConstituency(constituency.code);
      setCandidates(constituencyCandidates);
    } catch (error) {
      setError(t('nominations.loadError') || 'Failed to load candidates');
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCandidateClick = candidateId => {
    navigate(`/candidate/${candidateId}`);
  };

  const handleBackToMap = () => {
    setCurrentView('map');
    setSelectedConstituency(null);
    setCandidates([]);
    setError(null);
  };

  const getPartyColor = (partyShortName) => {
    return PARTY_COLORS[String(partyShortName).toUpperCase()] || PARTY_COLORS['DEFAULT'];
  };

  const getInitials = name => {
    if (!name) return '?';
    const names = name.trim().split(' ');
    if (names.length >= 2) {
      return names[0][0] + names[names.length - 1][0];
    }
    return names[0][0];
  };

  const getSymbolUrl = candidate => {
    return candidate.customSymbolUrl || candidate.symbolUrl || candidate.partySymbolUrl || null;
  };

  const renderBreadcrumbs = () => {
    if (currentView !== 'candidates') return null;
    return (
      <div className="breadcrumb-nav">
        <button onClick={handleBackToMap} className="breadcrumb-btn" title={t('common.map') || 'Map'}>
          <i className="fas fa-map" aria-hidden="true"></i>
          <span>{t('common.map') || 'Map'}</span>
        </button>
        <i className="fas fa-chevron-right" aria-hidden="true"></i>
        <span className="breadcrumb-current">{selectedConstituency?.name}</span>
      </div>
    );
  };

  const onSelectChange = (e) => {
    const code = e.target.value;
    if (!code) {
      setSelectedConstituency(null);
      setCandidates([]);
      setCurrentView('map');
      return;
    }
    const selected = constituencies.find(c => c.code === code || c.displayCode === code);
    if (selected) {
      handleConstituencyClick(selected);
    }
  };

  const partyStats = useMemo(() => {
    const stats = {};
    Object.values(winnerData).forEach(winner => {
      const party = winner.party;
      stats[party] = (stats[party] || 0) + 1;
    });
    return Object.entries(stats)
      .map(([party, count]) => ({
        party,
        count,
        percentage: ((count / Object.keys(winnerData).length) * 100).toFixed(2)
      }))
      .sort((a, b) => b.count - a.count);
  }, [winnerData]);

  // Sort candidates by votes, assign winner/runner-up, and extract safe voteCount
  const sortedCandidates = useMemo(() => {
    return [...candidates].sort((a, b) => {
      const getVotes = (c) => {
        if (c.rounds && Array.isArray(c.rounds) && c.rounds.length > 0) {
          const latestRound = c.rounds[c.rounds.length - 1];
          return typeof latestRound.votes === 'number' ? latestRound.votes : 0;
        }
        return typeof c.votes === 'number' ? c.votes : 0;
      };
      return getVotes(b) - getVotes(a);
    });
  }, [candidates]);

  const renderMapView = () => (
    <>
      <div className="map-header">
        <h1>{t('banner.title2') || 'Bihar Assembly Election 2025'}</h1>
        <p>{t('nominationsPage.instruction') || 'Click on a constituency to see details'}</p>
      </div>

<div className="constituency-select-wrapper">
  <label htmlFor="selectConstituency" className="constituency-select-label">
    Select Constituency:
  </label>
  <select
    id="selectConstituency"
    className="constituency-select"
    value={selectedConstituency?.code || ''}
    onChange={onSelectChange}
  >
    <option value="">-- Select Constituency --</option>
    {constituencies.map(c => (
      <option key={c.code} value={c.code}>
        {c.displayCode ? `${c.displayCode} - ` : ""}{c.name}
      </option>
    ))}
  </select>
</div>


      <section className="map-section">
        <div className="map-svg-container">
          {loading ? (
            <div className="loading-state">
              <div className="spinner-large"></div>
              <p>{t('common.loading') || 'Loading Map...'}</p>
            </div>
          ) : error ? (
            <div className="empty-state-full">
              <i className="fas fa-exclamation-triangle fa-3x" aria-hidden="true"></i>
              <h3>{t('common.errorLoading') || 'Error Loading Map'}</h3>
              <p>{error}</p>
              <button
                onClick={loadConstituenciesAndResults}
                className="details-btn-full"
                title={t('common.retry') || 'Retry'}
              >
                <i className="fas fa-redo" aria-hidden="true"></i> {t('common.retry') || 'Retry'}
              </button>
            </div>
          ) : (
            <>
              <BiharMapSVG
                constituencies={constituencies}
                winnerData={winnerData}
                partyColors={PARTY_COLORS}
                onConstituencyClick={handleConstituencyClick}
              />
              <div className="party-legend">
  <h4>पार्टी-वार समग्र प्रदर्शन</h4>
  <div className="legend-items">
    {partyStats.map(stat => (
      <div key={stat.party} className="legend-item" style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
        
        <span
          style={{
            display: 'inline-block',
            width: 16,
            height: 16,
            borderRadius: 4,
            backgroundColor: PARTY_COLORS[String(stat.party).toUpperCase()] || PARTY_COLORS['DEFAULT'],
            border: '1px solid #222',
            marginRight: 8
          }}
        ></span>
        <span style={{ fontWeight: 600, marginRight: 8 }}>{stat.party}</span>
        <span style={{ color: '#666', marginRight: 6 }}>{stat.count}</span>
        <span style={{ color: '#aaa' }}>({stat.percentage}%)</span>
      </div>
    ))}
  </div>
</div>
            </>
          )}
        </div>

        <div className="election-info-section">
          <div className="election-info-box">
            <div className="info-item">
              <strong>
                <i className="fas fa-poll" aria-hidden="true"></i> 
                {t('dashboard.totalConstituencies') || 'Total Constituencies:'}
              </strong>
              <span>{constituencies.length > 0 ? constituencies.length : '...'}</span>
            </div>
            <div className="info-item">
              <strong>
                <span className="phase-color-box phase-1-color"></span>
                {t('results.phase1') || 'Phase I:'}
              </strong>
              <span>{phase1Count} {t('nominations.constituencies') || 'Constituencies'} (06-11-2025)</span>
            </div>
            <div className="info-item">
              <strong>
                <span className="phase-color-box phase-2-color"></span>
                {t('results.phase2') || 'Phase II:'}
              </strong>
              <span>{phase2Count} {t('nominations.constituencies') || 'Constituencies'} (11-11-2025)</span>
            </div>
            <div className="info-item">
              <strong>
                <i className="fas fa-calendar-check" aria-hidden="true"></i> 
                {t('results.countingDate') || 'Counting Date:'}
              </strong>
              <span>14-11-2025</span>
            </div>
          </div>

          <div className="alert-news-box">
            <div className="alert-header">
              <i className="fas fa-bell" aria-hidden="true"></i>
              <h3>{t('dashboard.importantNotice') || 'Important Notice'}</h3>
              <span className="alert-badge">NEW</span>
            </div>
            <div className="alert-content">
              <p className="alert-message">
                <i className="fas fa-info-circle" aria-hidden="true"></i>
                {t('dashboard.electionScheduleNote') || 'View official election schedule'}
              </p>
              <div className="pdf-options">
                <a 
                  href="/pdfs/m_DM_list_details.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="pdf-btn schedule-btn"
                  title={t('dashboard.viewSchedulePDF') || 'Election Schedule'}
                >
                  <i className="fas fa-file-pdf" aria-hidden="true"></i>
                  <span>{t('dashboard.electionSchedule') || 'Election Schedule'}</span>
                  <i className="fas fa-external-link-alt" aria-hidden="true"></i>
                </a>
              </div>
              <div className="alert-footer">
                <small>
                  <i className="fas fa-clock" aria-hidden="true"></i>
                  {t('dashboard.lastUpdated') || 'Last Updated:'}{' '}
                  {new Date().toLocaleString('en-IN', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </small>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
const renderCandidatesView = () => (
    <section className="full-page-section">
      <div className="page-header">
        <h1>
          {selectedConstituency?.name} {t('nominations.candidates') || 'Candidates'}
        </h1>
        <p>
          <i className="fas fa-map-marker-alt" aria-hidden="true"></i> 
          {selectedConstituency?.district} {t('results.district') || 'District'} • 
          AC {t('nominations.noAbbr') || 'No.'} {selectedConstituency?.code}
        </p>
      </div>
      {loading ? (
        <div className="loading-state">
          <div className="spinner-large"></div>
          <p>{t('common.loading') || 'Loading candidates...'}</p>
        </div>
      ) : error ? (
        <div className="empty-state-full">
          <i className="fas fa-exclamation-triangle fa-3x" aria-hidden="true"></i>
          <h3>{t('common.errorLoading') || 'Error Loading Candidates'}</h3>
          <p>{error}</p>
          <button 
            onClick={() => handleConstituencyClick(selectedConstituency)} 
            className="details-btn-full"
            title={t('common.retry') || 'Retry'}
          >
            <i className="fas fa-redo" aria-hidden="true"></i> {t('common.retry') || 'Retry'}
          </button>
        </div>
      ) : sortedCandidates.length > 0 ? (
        <div className="candidates-grid-nomination-style">
  {sortedCandidates.map((candidate, idx) => {
  // Debug: Put a breakpoint in browser devtools and print candidate structure
  console.log("Candidate data:", candidate);
  // This will pause execution if dev tools are open (press F12, then reload and select a constituency)


  let voteCount = 0;
  if (
    candidate.rounds &&
    Array.isArray(candidate.rounds) &&
    candidate.rounds.length > 0
  ) {
    voteCount = candidate.rounds
      .map(r => typeof r.votes === "number" ? r.votes : 0)
      .reduce((max, curr) => Math.max(max, curr), 0);
  }
  if (!voteCount && typeof candidate.votes === "number") {
    voteCount = candidate.votes;
  }
  if (!voteCount && typeof candidate.totalVotes === "number") {
    voteCount = candidate.totalVotes;
  }
  if (!voteCount && typeof candidate.voteCount === "number") {
    voteCount = candidate.voteCount;
  }
  if (voteCount === undefined || voteCount === null || isNaN(voteCount)) voteCount = 0;

  return (
    <CandidateCard
      key={candidate.id}
      candidate={candidate}
      constituency={selectedConstituency}
      isWinner={idx === 0}
      isRunnerUp={idx === 1}
      voteCount={voteCount}
      onViewDetails={handleCandidateClick}
      partyColor={{
        primary: getPartyColor(candidate.partyShortName),
        secondary: getPartyColor(candidate.partyShortName)
      }}
      getFullImageUrl={getFullImageUrl}
      getInitials={getInitials}
      getSymbolUrl={getSymbolUrl}
      t={t}
    />
  );
})}

</div>

      ) : (
        <div className="empty-state-full">
          <i className="fas fa-user-times fa-3x" aria-hidden="true"></i>
          <h3>{t('common.noDataFound') || 'No Candidates Found'}</h3>
          <p>{t('nominations.noCandidatesInfo') || 'No candidate information available'}</p>
        </div>
      )}
    </section>
  );

  return (
    <div className="bihar-map-single-page">
      {renderBreadcrumbs()}
      {currentView === 'map' ? renderMapView() : renderCandidatesView()}
    </div>
  );
};

const CandidateCard = ({ 
  candidate, 
  constituency, 
  onViewDetails, 
  partyColor, 
  getFullImageUrl, 
  getInitials, 
  getSymbolUrl,
  voteCount = 0,
  isWinner = false,
  isRunnerUp = false,
  t,
}) => {
  const [imageError, setImageError] = useState(false);
  const [symbolError, setSymbolError] = useState(false);

  return (
    <div 
      className="candidate-card-nom" 
      onClick={() => onViewDetails(candidate.id)}
      style={{ borderLeftColor: isWinner ? "#43a047" : isRunnerUp ? "#fbc02d" : partyColor.primary, cursor: 'pointer', position: 'relative' }}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onViewDetails(candidate.id);
        }
      }}
    >
      {/* Winner/Runner up Badge */}
      {isWinner && (
    <div className="candidates-badge winners-badge">
      <span className="badges-icon"><i className="fas fa-trophy"></i></span>
      <span className="badges-label">Winner</span>
    </div>
  )}
  {!isWinner && isRunnerUp && (
    <div className="candidates-badge runnerups-badge">
      <span className="badges-icon"><i className="fas fa-medal"></i></span>
      <span className="badges-label">Runner Up</span>
    </div>
  )}


      <div className="card-header-nom">
        <div 
          className="candidate-photo-nom"
          style={{
            background: `linear-gradient(135deg, ${partyColor.primary}, ${partyColor.secondary})`
          }}
        >
          {candidate.photoUrl && !imageError ? (
            <img 
              src={getFullImageUrl(candidate.photoUrl)} 
              alt={candidate.name}
              onError={() => setImageError(true)}
            />
          ) : (
            <span className="initials-nom">{getInitials(candidate.name)}</span>
          )}
        </div>
        <div className="candidate-info-nom">
          <h3>{candidate.name}</h3>
          <div className="constituency-name-nom">
            {constituency.code} - {constituency.name}
          </div>
          <div className="district-name-nom">
            {t('results.district') || 'District'}: {constituency.district || t('common.noData') || 'N/A'}
          </div>
        </div>
      </div>
      <div className="party-info-bar-nom" style={{
        background: `linear-gradient(135deg, ${partyColor.primary}, ${partyColor.secondary})`
      }}>
        <div className="party-left-nom">
          <div className="party-badge-nom">{candidate.partyShortName}</div>
          <div className="party-full-name-nom">{candidate.partyName}</div>
        </div>
        {getSymbolUrl(candidate) && !symbolError && (
          <div className="party-symbol-badge-nom">
            <img 
              src={getFullImageUrl(getSymbolUrl(candidate))} 
              alt={`${candidate.partyShortName} symbol`}
              onError={() => setSymbolError(true)}
            />
          </div>
        )}
      </div>
      <div className="card-divider-nom"></div>
      <div className="candidate-details-nom">
        {/* Votes */}
        <div className="detail-item-nom" style={{background: '#e8f5e9', marginBottom: '7px', borderRadius: '7px'}}>
          <span className="detail-label-nom" style={{fontWeight: 800, color: '#153e21'}}>
            <i className="fas fa-vote-yea" aria-hidden="true" style={{color:'#388e3c'}}></i>
            {t('results.votes') || 'Votes'}
          </span>
          <span className="detail-value-nom" style={{color: '#1b5e20', fontWeight: 900}}>
            {(voteCount || 0).toLocaleString('en-IN')}
          </span>
        </div>

        {/* The rest unchanged */}
        <div className="detail-item-nom">
          <span className="detail-label-nom">
            <i className="fas fa-birthday-cake" aria-hidden="true"></i> 
            {t('results.age') || 'Age'}
          </span>
          <span className="detail-value-nom">{candidate.age || t('common.noData') || 'N/A'}</span>
        </div>
        <div className="detail-item-nom">
          <span className="detail-label-nom">
            <i className="fas fa-graduation-cap" aria-hidden="true"></i> 
            {t('results.education') || 'Education'}
          </span>
          <span className="detail-value-nom">{candidate.education || t('common.noData') || 'N/A'}</span>
        </div>
        <div className="detail-item-nom">
          <span className="detail-label-nom">
            <i className="fas fa-briefcase" aria-hidden="true"></i> 
            {t('results.profession') || 'Profession'}
          </span>
          <span className="detail-value-nom">{candidate.profession || t('common.noData') || 'N/A'}</span>
        </div>
        <div className="detail-item-nom">
          <span className="detail-label-nom">
            <i className="fas fa-money-bill-wave" aria-hidden="true"></i> 
            {t('results.assets') || 'Assets'}
          </span>
          <span className="detail-value-nom">
            {candidate.assets ? `₹${candidate.assets.toLocaleString('en-IN')}` : t('common.noData') || 'N/A'}
          </span>
        </div>
        <div className="detail-item-nom">
          <span className="detail-label-nom">
            <i className="fas fa-gavel" aria-hidden="true"></i> 
            {t('results.criminalCases') || 'Criminal Cases'}
          </span>
          <span className={`criminal-badge-nom ${candidate.criminalCases > 0 ? '' : 'no-cases'}`}>
            {candidate.criminalCases || 0} 
            {candidate.criminalCases === 0 ? ` ✓ ${t('results.noCases') || ''}` : ''}
          </span>
        </div>
      </div>
      <button 
        className="view-details-btn-nom"
        style={{
          background: `linear-gradient(135deg, ${partyColor.primary}, ${partyColor.secondary})`
        }}
        onClick={(e) => {
          e.stopPropagation();
          onViewDetails(candidate.id);
        }}
        title={t('common.viewDetails') || 'View Details'}
      >
        <i className="fas fa-info-circle" aria-hidden="true"></i> 
        {t('common.viewDetails') || 'View Details'}
      </button>
    </div>
  );
};

export default BiharElectionMap;